/**
 * fmr-cart.js
 * Shared cart helper for Framar LP sections.
 *
 * After every add it dispatches Horizon's native `cart:update` event so the
 * cart drawer opens automatically — no legacy addProduct() / addMultipleProducts()
 * dependency needed anywhere in the theme.
 *
 * Public API (also usable programmatically):
 *   window.FmrCart.add(variantId, qty, btn)       – single item
 *   window.FmrCart.addMultiple(items, btn, options) – [{id, quantity}, ...]
 *   options.sections / options.sections_url     – optional native cart section rendering
 *
 * Declarative hooks (zero-JS sections, just add data-* attributes):
 *   <button data-fmr-atc="VARIANT_ID" data-fmr-qty="1">         – static variant
 *   <button data-fmr-atc-src="INPUT_ELEMENT_ID" data-fmr-qty="1"> – dynamic (reads hidden input)
 *   <button data-fmr-multi-atc data-fmr-items='[{"id":x,"quantity":1}]'> – bundle
 *   <form   data-fmr-cart-form>                                  – standard form intercept
 *
 * Button state labels (optional, fall back to "Adding…" / "Added!"):
 *   data-fmr-loading="Adding…"   data-fmr-added="Added!"
 */
(function () {
  'use strict';

  if (window.FmrCart) return;

  // ── Horizon cart:update event ─────────────────────────────────────────────
  // cart-drawer.js listens for this event name and opens the drawer.
  function dispatchCartUpdate(cart, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          data: { didError: false, source: 'fmr-cart', itemCount: cart.item_count, sections: sections }
        }
      })
    );
  }

  function fetchCart() {
    return fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // ── Button state management ───────────────────────────────────────────────
  function setButtonState(btn, state) {
    if (!btn) return;
    switch (state) {
      case 'loading':
        if (!btn.dataset.fmrOrig) btn.dataset.fmrOrig = btn.textContent.trim();
        btn.disabled = true;
        btn.textContent = btn.getAttribute('data-fmr-loading') || 'Adding…';
        break;
      case 'added':
        btn.disabled = true;
        btn.textContent = btn.getAttribute('data-fmr-added') || 'Added!';
        break;
      case 'error':
        btn.disabled = false;
        btn.textContent = 'Try again';
        break;
      case 'reset':
        btn.disabled = false;
        if (btn.dataset.fmrOrig) {
          btn.textContent = btn.dataset.fmrOrig;
          delete btn.dataset.fmrOrig;
        }
        break;
    }
  }

  // ── Core fetch ────────────────────────────────────────────────────────────
  function postItems(items, btn, options) {
    setButtonState(btn, 'loading');
    var body = { items: items };
    var sections;
    if (options && options.sections && options.sections.length) {
      body.sections = options.sections;
      body.sections_url = options.sections_url || window.location.pathname;
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) throw new Error(data.description || 'Cart add failed');
        sections = data.sections;
        setButtonState(btn, 'added');
        return fetchCart();
      })
      .then(function (cart) {
        dispatchCartUpdate(cart, sections);
        return cart;
      })
      .catch(function (err) {
        console.error('[FmrCart]', err.message || err);
        setButtonState(btn, 'error');
      })
      .finally(function () {
        setTimeout(function () { setButtonState(btn, 'reset'); }, 2400);
      });
  }

  // ── Public API ────────────────────────────────────────────────────────────
  var FmrCart = {
    add: function (variantId, qty, btn) {
      return postItems([{ id: variantId, quantity: qty || 1 }], btn);
    },
    addMultiple: function (items, btn, options) {
      return postItems(items, btn, options);
    }
  };

  window.FmrCart = FmrCart;

  // ── Click delegation: single item [data-fmr-atc] / [data-fmr-atc-src] ──────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fmr-atc], [data-fmr-atc-src]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var variantId;
    var srcId = btn.getAttribute('data-fmr-atc-src');
    if (srcId) {
      var input = document.getElementById(srcId);
      variantId = input ? parseInt(input.value, 10) : 0;
    } else {
      variantId = parseInt(btn.getAttribute('data-fmr-atc'), 10);
    }

    if (!variantId || variantId <= 0 || isNaN(variantId)) return;
    FmrCart.add(variantId, parseInt(btn.getAttribute('data-fmr-qty') || '1', 10), btn);
  });

  // ── Click delegation: multi-item bundle [data-fmr-multi-atc] ─────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fmr-multi-atc]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var raw = btn.getAttribute('data-fmr-items');
    if (!raw) return;
    var items;
    try { items = JSON.parse(raw); } catch (_) { return; }
    if (!items || !items.length) return;
    FmrCart.addMultiple(items, btn);
  });

  // ── Submit delegation: [data-fmr-cart-form] ──────────────────────────────
  // Used by extra-dirty sections (plain forms, no existing JS interception).
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-fmr-cart-form]');
    if (!form) return;
    e.preventDefault();

    var idInput  = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    if (!idInput) return;

    var variantId = parseInt(idInput.value, 10);
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    if (!variantId || variantId <= 0 || isNaN(variantId)) return;

    var btn = form.querySelector('[type="submit"]');
    FmrCart.add(variantId, qty, btn);
  });
})();
